import { DOCUMENT } from '@angular/common';
import { effect, inject, Injectable, signal } from '@angular/core';
import { DATA_THEME_ATTRIBUTE, STORAGE_KEY } from '@common/constants';
import { Theme } from '@common/enums';
import { LocalStorageService } from '@common/services/storage/local-storage.service';
import { WINDOW } from '@common/window.provider';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly storageService = inject(LocalStorageService);
  private readonly window = inject(WINDOW);

  private readonly initialized = signal(false);
  public readonly theme = signal<Theme>(Theme.Light);

  private readonly syncThemeEffect = effect(() => {
    if (!this.initialized()) return;

    const theme = this.theme();

    this.document.documentElement.setAttribute(DATA_THEME_ATTRIBUTE, theme);
    this.storageService.set(STORAGE_KEY, theme);
  });

  init(): void {
    this.theme.set(this.storageService.get<Theme>(STORAGE_KEY) ?? this.getPreferredTheme());
    this.initialized.set(true);
  }

  toggle(): void {
    this.theme.update((theme) => (theme === Theme.Dark ? Theme.Light : Theme.Dark));
  }

  private getPreferredTheme(): Theme {
    if (typeof this.window.matchMedia !== 'function') {
      return Theme.Light;
    }

    return this.window.matchMedia('(prefers-color-scheme: dark)').matches ? Theme.Dark : Theme.Light;
  }
}
